import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  fallback?: ReactNode;
  children: ReactNode;
  resetKey?: string | number;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Per-route error boundary. Wrap each top-level route's element with this so a
 * single broken page does not blank the entire app shell. Pair it with
 * `Sentry.captureException` for production observability.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (typeof window !== 'undefined') {
      const sentry = window.Sentry;
      sentry?.captureException?.(error, { extra: { errorInfo } });
    }
    if (typeof console !== 'undefined') {
      console.error('[error_boundary]', error, errorInfo);
    }
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div role="alert" className="min-h-[40vh] flex items-center justify-center p-8 text-center">
          <div>
            <h1 className="text-2xl font-black mb-2">Something went wrong.</h1>
            <p className="text-sm text-zinc-500 mb-4">Try refreshing the page. The team has been notified.</p>
            <button
              type="button"
              onClick={this.handleReload}
              className="bg-amber-400 text-black font-bold px-4 py-2 rounded"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
